import axios from "axios";

import { BASE_URL } from "./apiBase";

const withAuth = (token) => {
  const bearerToken =
    typeof token === "string" && token.trim() ? token.trim() : null;
  return {
    headers: bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {},
    withCredentials: true,
  };
};

export const getAdminUsers = async (token, params = {}) => {
  const response = await axios.get(`${BASE_URL}/api/admin/users`, {
    ...withAuth(token),
    params,
  });
  return response.data.items ?? response.data;
};

export const approveVillager = async (token, userId) => {
  await axios.patch(
    `${BASE_URL}/api/Admin/villagers/${userId}/approve`,
    {},
    withAuth(token)
  );
};

export const updateUserStatus = async (token, userId, isActive) => {
  await axios.patch(
    `${BASE_URL}/api/admin/users/${userId}/status`,
    { isActive },
    withAuth(token)
  );
};

export const updateUserRole = async (token, userId, role) => {
  await axios.patch(
    `${BASE_URL}/api/admin/users/${userId}/role`,
    { role },
    withAuth(token)
  );
};

export const getAdminFarms = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/admin/farms`, withAuth(token));
  return response.data.items ?? response.data;
};

export const createFarm = async (token, payload) => {
  const response = await axios.post(
    `${BASE_URL}/api/admin/farms`,
    payload,
    withAuth(token)
  );
  return response.data;
};

export const updateFarm = async (token, id, payload) => {
  const response = await axios.put(
    `${BASE_URL}/api/admin/farms/${id}`,
    payload,
    withAuth(token)
  );
  return response.data;
};

export const deleteFarm = async (token, id) => {
  await axios.delete(`${BASE_URL}/api/admin/farms/${id}`, withAuth(token));
};

export const getAdminProducts = async (token, farmId) => {
  const response = await axios.get(`${BASE_URL}/api/admin/products`, {
    ...withAuth(token),
    params: farmId ? { farmId } : {},
  });
  return response.data.items ?? response.data;
};

export const createProduct = async (token, payload) => {
  const response = await axios.post(
    `${BASE_URL}/api/admin/products`,
    payload,
    withAuth(token)
  );
  return response.data;
};

export const updateProduct = async (token, id, payload) => {
  const response = await axios.put(
    `${BASE_URL}/api/admin/products/${id}`,
    payload,
    withAuth(token)
  );
  return response.data;
};

export const deleteProduct = async (token, id) => {
  await axios.delete(`${BASE_URL}/api/admin/products/${id}`, withAuth(token));
};

export const getAdminBaskets = async (token, farmId) => {
  const response = await axios.get(`${BASE_URL}/api/admin/baskets`, {
    ...withAuth(token),
    params: farmId ? { farmId } : {},
  });
  return response.data.items ?? response.data;
};

export const createBasket = async (token, payload) => {
  const response = await axios.post(
    `${BASE_URL}/api/admin/baskets`,
    payload,
    withAuth(token)
  );
  return response.data;
};

export const updateBasket = async (token, id, payload) => {
  const response = await axios.put(
    `${BASE_URL}/api/admin/baskets/${id}`,
    payload,
    withAuth(token)
  );
  return response.data;
};

export const deleteBasket = async (token, id) => {
  await axios.delete(`${BASE_URL}/api/admin/baskets/${id}`, withAuth(token));
};

export const getAdminOrders = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/admin/orders`, withAuth(token));
  return response.data.items ?? response.data;
};

export const updateOrderStatus = async (token, id, status) => {
  await axios.patch(
    `${BASE_URL}/api/admin/orders/${id}/status`,
    { status },
    withAuth(token)
  );
};

export const getSettings = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/admin/settings`, withAuth(token));
  return response.data;
};

export const updateSettings = async (token, settings) => {
  const response = await axios.put(
    `${BASE_URL}/api/admin/settings`,
    settings,
    withAuth(token)
  );
  return response.data;
};

// ─── Audience: newsletter subscribers + contact enquiries ────────────────────
/** Newsletter opt-ins. Inactive rows are the people who unsubscribed — kept, never deleted,
 *  so withdrawal of consent stays provable. */
export const getNewsletterSubscribers = async (token, { includeInactive = false } = {}) => {
  const response = await axios.get(`${BASE_URL}/api/Newsletter`, {
    ...withAuth(token),
    params: { includeInactive },
  });
  return response.data.items ?? response.data;
};

/** Contact-form enquiries, newest first. Stored on submit, so these survive a mail outage. */
export const getContactMessages = async (token, { take = 200 } = {}) => {
  const response = await axios.get(`${BASE_URL}/api/Contact`, {
    ...withAuth(token),
    params: { take },
  });
  return response.data.items ?? response.data;
};

// ─── Payouts: what we owe each farm, and settling it ─────────────────────────

/** One row per farm per delivered order. Amounts are in ALL. */
export const getPayouts = async (token, { farmId, status, page = 1, pageSize = 100 } = {}) => {
  const response = await axios.get(`${BASE_URL}/api/admin/payouts`, {
    ...withAuth(token),
    params: { farmId, status, page, pageSize },
  });
  return response.data;
};

/** Per-farm totals: earned, our commission, still owed, already transferred. */
export const getPayoutSummary = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/admin/payouts/summary`, withAuth(token));
  return response.data;
};

export const getCommissionRate = async (token) => {
  const response = await axios.get(
    `${BASE_URL}/api/admin/payouts/commission-rate`,
    withAuth(token)
  );
  return response.data.rate;
};

/** Mark a payout as transferred to the farm. */
export const settlePayout = async (token, id, reference) => {
  const response = await axios.patch(
    `${BASE_URL}/api/admin/payouts/${id}/settle`,
    { reference },
    withAuth(token)
  );
  return response.data;
};

/** Write payout rows for orders delivered before the ledger existed. Safe to repeat. */
export const backfillPayouts = async (token) => {
  const response = await axios.post(
    `${BASE_URL}/api/admin/payouts/backfill`,
    {},
    withAuth(token)
  );
  return response.data;
};

/**
 * Record that an order's money arrived, or that it was refunded. This is the only way a bank
 * transfer is ever settled — nothing on the fulfilment path can observe it.
 */
export const recordOrderPayment = async (token, orderId, { paymentStatus, amountPaid, reference }) => {
  const response = await axios.patch(
    `${BASE_URL}/api/admin/orders/${orderId}/payment`,
    { paymentStatus, amountPaid, reference },
    withAuth(token)
  );
  return response.data;
};
